import { useEffect, useMemo, useRef, useState } from "react";
import { admin, followRun, type LogLine, type Run } from "../../api/admin";
import { Counters, Progress, StateBadge, took, StopRun, Linkified } from "./bits";

/** A run's live log, followed over SSE and left on screen when it finishes. */
export function RunLog({ id, onClose }: { id: number; onClose: () => void }) {
  const [run, setRun] = useState<Run | null>(null);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [stick, setStick] = useState(true);
  // A filter on the drawer, not a second request: the lines are already here,
  // and a crawl that logs four a second is unreadable without one.
  const [only, setOnly] = useState("");
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLines([]); setLive(true); setError(null);
    admin.runDetail(id).then(setRun).catch((e: Error) => setError(e.message));

    const abort = new AbortController();
    followRun(id, {
      log: (l) => setLines((ls) => (ls.length > 5000 ? [...ls.slice(-4000), l] : [...ls, l])),
      state: (s) => setRun((r) => (r ? { ...r, ...s } : r)),
      end: () => {
        setLive(false);
        admin.runDetail(id).then(setRun).catch(() => { /* the stream already said it ended */ });
      },
    }, abort.signal).catch((e: Error) => {
      if (abort.signal.aborted) return;
      setError(e.message); setLive(false);
    });
    return () => abort.abort();
  }, [id]);

  // What this run has actually written, most-used first, so the filter offers
  // the categories in front of you rather than the whole vocabulary.
  const seen = useMemo(() => {
    const n = new Map<string, number>();
    for (const l of lines) n.set(l.category, (n.get(l.category) ?? 0) + 1);
    return [...n].sort((a, b) => b[1] - a[1]);
  }, [lines]);
  const shown = only ? lines.filter((l) => l.category === only) : lines;

  // Follow the tail, unless the reader has scrolled up to look at something.
  useEffect(() => {
    if (stick && box.current) box.current.scrollTop = box.current.scrollHeight;
  }, [shown, stick]);

  return (
    <div className="runlog">
      <header>
        <b>Run {id}</b>
        {run && <><code>{run.kind}</code><StateBadge state={run.state} /></>}
        {live && <span className="dim"><span className="spinner" />following</span>}
        {run && <span className="dim">{took(run)}</span>}
        {run && <StopRun run={run} />}
        <button className="x" onClick={onClose} aria-label="close">×</button>
      </header>

      {run && <div className="runmeta"><Progress run={run} /><Counters counters={run.counters} /></div>}
      {seen.length > 1 && (
        <div className="runmeta">
          <select value={only} onChange={(e) => setOnly(e.target.value)}>
            <option value="">Every category ({lines.length})</option>
            {seen.map(([c, n]) => <option key={c} value={c}>{c} ({n})</option>)}
          </select>
        </div>
      )}
      {run?.error && <div className="error">{run.error}</div>}
      {error && <div className="error">{error}</div>}

      <div
        className="lines"
        ref={box}
        onScroll={(e) => {
          const el = e.currentTarget;
          setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
      >
        {shown.length === 0 && (
          <div className="dim small">{lines.length === 0 ? "no output yet" : "nothing in that category"}</div>
        )}
        {shown.map((l) => (
          <div key={l.id} className={`line ${l.level}`}>
            <span className="t">{new Date(l.at).toLocaleTimeString()}</span>
            <button
              className={`cat ${l.level}`}
              title={`show only ${l.category}`}
              onClick={() => setOnly(only === l.category ? "" : l.category)}
            >
              {l.category}
            </button>
            <span className="m"><Linkified text={l.msg} /></span>
            {l.fields && (
              <span className="f">
                {Object.entries(l.fields).map(([k, v]) => (
                  <span key={k}>{k}=<b><Linkified text={String(v)} /></b></span>
                ))}
              </span>
            )}
          </div>
        ))}
      </div>
      {!stick && (
        <button className="tailbtn" onClick={() => setStick(true)}>Follow the tail ↓</button>
      )}
    </div>
  );
}
